import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import {
  Calendar,
  MapPin,
  AlertCircle,
  Package,
  Users,
  Square,
  ChevronLeft,
  ChevronRight,
  MessageCircleX,
  Truck,
  PanelLeftClose,
  PanelRightClose,
  X,
  Navigation,
} from 'lucide-react';
import DateSelector from './DateSelector';
import DeliveryPointsList from './DeliveryPointsList';
import OrdersList from './OrdersList';
import DriversList from './DriversList';
import UsersList from './UsersList';
import MissingLocationModal from './MissingLocationModal';
import DriverSelectionModal from './DriverSelectionModal';
import ControlTowerMap from './ControlTowerMap';
import SearchBar from './SearchBar';
import DetailModal from './DetailModal';
import UserDetailModal from './modals/UserDetailModal';
import { NoResponseModal } from './modals/NoResponseModal';
import WhatsAppLocationsModal from './WhatsAppLocationsModal';

const API_URL = process.env.REACT_APP_API_URL;

function countByStatus(items) {
  return items.reduce((acc, item) => {
    const status = item.status || 'unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
}

function StatBadge({ icon: Icon, label, value, colorClass = 'text-[#B1336E]' }) {
  return (
    <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1 shadow-sm">
      <Icon className={`h-3.5 w-3.5 ${colorClass}`} />
      <span className="text-xs font-bold text-slate-800 font-montserrat">{value}</span>
      <span className="hidden text-[11px] font-medium text-slate-500 font-montserrat sm:inline">
        {label}
      </span>
    </div>
  );
}

function TorreDeControl({ setActiveComponent }) {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCalendar, setShowCalendar] = useState(false);

  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [missingLocationPoints, setMissingLocationPoints] = useState([]);

  const [activeView, setActiveView] = useState('deliveries');
  const [showDeliveryMarkers, setShowDeliveryMarkers] = useState(true);
  const [showOrderMarkers, setShowOrderMarkers] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);

  const [showMissingLocationModal, setShowMissingLocationModal] = useState(false);
  const [showDriverSelectionModal, setShowDriverSelectionModal] = useState(false);
  const [selectedPointsForDriver, setSelectedPointsForDriver] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [noResponseCount, setNoResponseCount] = useState(null);
  const [showNoResponseModal, setShowNoResponseModal] = useState(false);
  const [whatsAppLocationAlertCount, setWhatsAppLocationAlertCount] = useState(0);
  const [showWhatsAppLocationsModal, setShowWhatsAppLocationsModal] = useState(false);
  const [locationAlertRefreshTick, setLocationAlertRefreshTick] = useState(0);

  const [showLeftList, setShowLeftList] = useState(true);
  const [showRightList, setShowRightList] = useState(true);
  const [mobilePanel, setMobilePanel] = useState('deliveries');

  const socketRef = useRef(null);

  const deliveryCounts = useMemo(() => countByStatus(deliveryPoints), [deliveryPoints]);
  const orderCounts = useMemo(() => countByStatus(orders), [orders]);
  const activeDriversCount = useMemo(
    () => drivers.filter((driver) => driver.latitude && driver.longitude).length,
    [drivers]
  );

  const handleItemClick = useCallback((id, type) => {
    if (type === 'user') {
      setSelectedUserId(id);
      setSelectedItemId(null);
      setSelectedItemType(null);
    } else {
      setSelectedItemId(id);
      setSelectedItemType(type);
      setSelectedUserId(null);
    }
  }, []);

  useEffect(() => {
    const formattedDate = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Lima',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

    setSelectedDate(formattedDate);
  }, []);

  const fetchDeliveryPoints = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/delivery_points/${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDeliveryPoints(response.data);
      setMissingLocationPoints(response.data.filter((dp) => !dp.latitude || !dp.longitude));
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    }
  }, [selectedDate]);

  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/orders/${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  }, [selectedDate]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/users/${selectedDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [selectedDate]);

  const fetchDriverLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/driver/location`);
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching driver locations:', error);
    }
  }, []);

  const fetchWhatsAppLocationAlertCount = useCallback(async () => {
    if (!selectedDate) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/control_tower/whatsapp-location-alerts/${selectedDate}`
      );
      setWhatsAppLocationAlertCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching WhatsApp location alert count:', error);
      setWhatsAppLocationAlertCount(0);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      fetchDeliveryPoints();
      fetchOrders();
      fetchUsers();
    }
  }, [selectedDate, fetchDeliveryPoints, fetchOrders, fetchUsers]);

  useEffect(() => {
    fetchWhatsAppLocationAlertCount();
  }, [fetchWhatsAppLocationAlertCount, locationAlertRefreshTick]);

  useEffect(() => {
    socketRef.current = io(API_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
    });

    socketRef.current.on('delivery_point_updated', (data) => {
      setDeliveryPoints((prev) => {
        const updated = prev.map((dp) => (dp.id === data.id ? { ...dp, ...data } : dp));
        setMissingLocationPoints(updated.filter((dp) => !dp.latitude || !dp.longitude));
        return updated;
      });
    });

    socketRef.current.on('order_updated', (payload) => {
      if (payload.data) {
        setOrders((prev) =>
          prev.map((order) =>
            order.id === payload.order_id ? { ...order, ...payload.data } : order
          )
        );
      }
    });

    socketRef.current.on('driver_location_updated', (data) => {
      setDrivers((prev) => {
        const existingIndex = prev.findIndex((driver) => driver.id === data.id);

        if (existingIndex >= 0) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            latitude: data.latitude,
            longitude: data.longitude,
          };
          return updated;
        }

        return [...prev, data];
      });
    });

    socketRef.current.on('delivery_location_alert_updated', () => {
      setLocationAlertRefreshTick((prev) => prev + 1);
    });

    socketRef.current.on('delivery_location_alert_reviewed', () => {
      setLocationAlertRefreshTick((prev) => prev + 1);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off('connect_error');
        socketRef.current.off('delivery_point_updated');
        socketRef.current.off('order_updated');
        socketRef.current.off('driver_location_updated');
        socketRef.current.off('delivery_location_alert_updated');
        socketRef.current.off('delivery_location_alert_reviewed');
        socketRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    fetchDriverLocations();
  }, [fetchDriverLocations]);

  useEffect(() => {
    setNoResponseCount(null);
  }, [selectedDate]);

  const activateDeliveries = useCallback(() => {
    setShowDeliveryMarkers(true);
    setShowOrderMarkers(false);
    setActiveView('deliveries');
    setMobilePanel('deliveries');
    setIsDrawingMode(false);
  }, []);

  const activateOrders = useCallback(() => {
    setShowDeliveryMarkers(false);
    setShowOrderMarkers(true);
    setActiveView('orders');
    setMobilePanel('orders');
    setIsDrawingMode(false);
  }, []);

  const activateUsers = useCallback(() => {
    setShowDeliveryMarkers(false);
    setShowOrderMarkers(false);
    setActiveView('users');
    setMobilePanel('users');
    setIsDrawingMode(false);
  }, []);

  const activateDrivers = useCallback(() => {
    setMobilePanel('drivers');
  }, []);

  const handleDrawingComplete = useCallback((selectedIds) => {
    setSelectedPointsForDriver(selectedIds);
    setShowDriverSelectionModal(true);
    setIsDrawingMode(false);
  }, []);

  const handleDriverAssignment = async (driverId) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint =
        activeView === 'deliveries'
          ? '/api/control_tower/assign_driver_deliveries'
          : '/api/control_tower/assign_driver_orders';

      await axios.post(
        `${API_URL}${endpoint}`,
        { ids: selectedPointsForDriver, driver_id: driverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (activeView === 'deliveries') {
        fetchDeliveryPoints();
      } else {
        fetchOrders();
      }

      setShowDriverSelectionModal(false);
      setSelectedPointsForDriver([]);
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Error al asignar conductor');
    }
  };

  const handleDetailDeleted = (itemId, itemType) => {
    if (itemType === 'delivery') {
      setDeliveryPoints((prev) => prev.filter((point) => point.id !== itemId));
      setMissingLocationPoints((prev) => prev.filter((point) => point.id !== itemId));
    } else if (itemType === 'order') {
      setOrders((prev) => prev.filter((order) => order.id !== itemId));
    }
  };

  useEffect(() => {
    function handleModalEvent(event) {
      const { id, type } = event.detail;
      handleItemClick(id, type);
    }

    function handleOpenUserDetailModal(event) {
      const { userId } = event.detail;
      setSelectedItemId(null);
      setSelectedItemType(null);
      setSelectedUserId(userId);
    }

    window.addEventListener('openDetailModal', handleModalEvent);
    window.addEventListener('openUserDetailModal', handleOpenUserDetailModal);
    return () => {
      window.removeEventListener('openDetailModal', handleModalEvent);
      window.removeEventListener('openUserDetailModal', handleOpenUserDetailModal);
    };
  }, [handleItemClick]);

  const leftList = (
    <>
      {activeView === 'deliveries' && (
        <DeliveryPointsList
          deliveryPoints={deliveryPoints}
          onItemClick={(id) => handleItemClick(id, 'delivery')}
        />
      )}
      {activeView === 'orders' && (
        <OrdersList orders={orders} onItemClick={(id) => handleItemClick(id, 'order')} />
      )}
      {activeView === 'users' && (
        <UsersList users={users} onItemClick={(id) => handleItemClick(id, 'user')} />
      )}
    </>
  );

  const mobileList = (
    <>
      {mobilePanel === 'drivers' ? (
        <DriversList
          drivers={drivers}
          deliveryPoints={deliveryPoints}
          orders={orders}
          activeView={activeView}
        />
      ) : (
        leftList
      )}
    </>
  );

  const viewButtons = [
    {
      id: 'deliveries',
      label: 'Entregas',
      icon: MapPin,
      active: activeView === 'deliveries' && mobilePanel !== 'drivers',
      onClick: activateDeliveries,
      count: deliveryPoints.length,
    },
    {
      id: 'orders',
      label: 'Recojos',
      icon: Package,
      active: activeView === 'orders' && mobilePanel !== 'drivers',
      onClick: activateOrders,
      count: orders.length,
    },
    {
      id: 'users',
      label: 'Usuarios',
      icon: Users,
      active: activeView === 'users' && mobilePanel !== 'drivers',
      onClick: activateUsers,
      count: users.length,
    },
    {
      id: 'drivers',
      label: 'Conductores',
      icon: Truck,
      active: mobilePanel === 'drivers',
      onClick: activateDrivers,
      count: drivers.length,
    },
  ];

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-100 font-montserrat">
      <header className="relative z-50 border-b border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 px-3 py-3 lg:px-4">
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={() => setActiveComponent('OptionsAdmin')}
              className="flex min-w-0 items-center gap-2 rounded-md px-2 py-2 text-slate-700 transition-colors hover:bg-slate-100 hover:text-[#B1336E]"
            >
              <ChevronLeft className="h-5 w-5 flex-shrink-0" />
              <span className="hidden text-sm font-semibold sm:inline">Volver</span>
            </button>

            <div className="min-w-0 flex-1 text-center lg:text-left">
              <h1 className="truncate text-base font-bold text-[#B1336E] sm:text-xl">
                Torre de Control
              </h1>
              <p className="text-xs font-medium text-slate-500">{selectedDate}</p>
            </div>

            <div className="hidden items-center gap-2 lg:flex">
              <SearchBar activeView={activeView} onResultClick={handleItemClick} />
            </div>

            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setShowCalendar((prev) => !prev)}
                className="rounded-md bg-slate-100 p-2 text-slate-700 transition-colors hover:bg-[#B1336E]/10 hover:text-[#B1336E]"
                title="Seleccionar fecha"
              >
                <Calendar className="h-5 w-5" />
              </button>
              <button
                onClick={() => setShowMissingLocationModal(true)}
                className="relative rounded-md bg-slate-100 p-2 text-slate-700 transition-colors hover:bg-red-100 hover:text-red-600"
                title="Puntos sin ubicacion"
              >
                <AlertCircle className="h-5 w-5" />
                {missingLocationPoints.length > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                    {missingLocationPoints.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setShowNoResponseModal(true)}
                className="relative rounded-md bg-slate-100 p-2 text-slate-700 transition-colors hover:bg-orange-100 hover:text-orange-600"
                title="Clientes sin respuesta"
              >
                <MessageCircleX className="h-5 w-5" />
                {noResponseCount > 0 && (
                  <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-orange-500" />
                )}
              </button>
              <button
                onClick={() => setShowWhatsAppLocationsModal(true)}
                className="relative rounded-md bg-slate-100 p-2 text-slate-700 transition-colors hover:bg-[#B1336E]/10 hover:text-[#B1336E]"
                title="Ubicaciones enviadas por WhatsApp"
              >
                <Navigation className="h-5 w-5" />
                {whatsAppLocationAlertCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B1336E] px-1 text-[10px] font-bold text-white">
                    {whatsAppLocationAlertCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsDrawingMode((prev) => !prev)}
                className={`rounded-md p-2 transition-colors ${
                  isDrawingMode
                    ? 'bg-[#67A43C] text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-[#67A43C]/10 hover:text-[#67A43C]'
                }`}
                title="Modo seleccion rectangular"
              >
                <Square className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 lg:hidden">
            <div className="min-w-[220px] flex-1">
              <SearchBar activeView={activeView} onResultClick={handleItemClick} />
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <StatBadge icon={MapPin} label="entregas" value={deliveryPoints.length} />
            <StatBadge icon={Package} label="recojos" value={orders.length} colorClass="text-[#E6B43C]" />
            <StatBadge icon={Users} label="usuarios" value={users.length} colorClass="text-[#67A43C]" />
            <StatBadge icon={Truck} label="en linea" value={activeDriversCount} colorClass="text-blue-600" />
            {deliveryCounts.pending > 0 && (
              <span className="rounded-md bg-yellow-100 px-2 py-1 text-[11px] font-semibold text-yellow-800">
                Pend. {deliveryCounts.pending}
              </span>
            )}
            {orderCounts.pending > 0 && (
              <span className="rounded-md bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800">
                Recojos pend. {orderCounts.pending}
              </span>
            )}
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            {viewButtons.slice(0, 3).map(({ id, label, icon: Icon, active, onClick, count }) => (
              <button
                key={id}
                onClick={onClick}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
                  active
                    ? 'bg-[#B1336E] text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-[#B1336E]/10 hover:text-[#B1336E]'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
                <span className="rounded bg-white/20 px-1.5 text-xs">{count}</span>
              </button>
            ))}
          </div>
        </div>

        {showCalendar && (
          <div className="fixed right-3 top-20 z-[70] sm:top-16">
            <DateSelector
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              showCalendar={showCalendar}
              onToggleCalendar={() => setShowCalendar((prev) => !prev)}
            />
          </div>
        )}
      </header>

      <main className="relative flex min-h-0 flex-1 overflow-hidden">
        <aside
          className={`relative z-20 hidden flex-shrink-0 overflow-hidden border-r border-slate-200 bg-white transition-all duration-300 ease-in-out lg:flex ${
            showLeftList ? 'w-80' : 'w-0'
          }`}
        >
          <div className={`h-full w-80 transition-opacity duration-200 ${showLeftList ? 'opacity-100' : 'opacity-0'}`}>
            {leftList}
          </div>
        </aside>

        <button
          onClick={() => setShowLeftList((prev) => !prev)}
          className="absolute top-4 z-30 hidden rounded-md border border-slate-200 bg-white p-2 text-slate-600 shadow-lg transition-all hover:bg-slate-50 lg:block"
          style={{ left: showLeftList ? 328 : 8 }}
          title={showLeftList ? 'Ocultar lista' : 'Mostrar lista'}
        >
          {showLeftList ? <PanelLeftClose className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        <section className="relative z-0 min-w-0 flex-1 bg-slate-200">
          <ControlTowerMap
            deliveryPoints={deliveryPoints}
            orders={orders}
            drivers={drivers}
            showDeliveryMarkers={showDeliveryMarkers}
            showOrderMarkers={showOrderMarkers}
            isDrawingMode={isDrawingMode}
            onDrawingComplete={handleDrawingComplete}
            onMarkerClick={handleItemClick}
            sidebarState={`${showLeftList ? '1' : '0'}-${showRightList ? '1' : '0'}`}
          />
        </section>

        <aside
          className={`relative z-20 hidden flex-shrink-0 overflow-hidden border-l border-slate-200 bg-white transition-all duration-300 ease-in-out lg:flex ${
            showRightList ? 'w-80' : 'w-0'
          }`}
        >
          <div className={`h-full w-80 transition-opacity duration-200 ${showRightList ? 'opacity-100' : 'opacity-0'}`}>
            <DriversList
              drivers={drivers}
              deliveryPoints={deliveryPoints}
              orders={orders}
              activeView={activeView}
            />
          </div>
        </aside>

        <button
          onClick={() => setShowRightList((prev) => !prev)}
          className="absolute right-2 top-4 z-30 hidden rounded-md border border-slate-200 bg-white p-2 text-slate-600 shadow-lg transition-all hover:bg-slate-50 lg:block"
          style={{ right: showRightList ? 328 : 8 }}
          title={showRightList ? 'Ocultar conductores' : 'Mostrar conductores'}
        >
          {showRightList ? <PanelRightClose className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>

        <div className="absolute inset-x-3 bottom-3 z-30 lg:hidden">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
            <div className="grid grid-cols-4 border-b border-slate-200">
              {viewButtons.map(({ id, label, icon: Icon, active, onClick, count }) => (
                <button
                  key={id}
                  onClick={onClick}
                  className={`flex min-w-0 flex-col items-center gap-0.5 px-1 py-2 text-[10px] font-semibold transition-colors ${
                    active ? 'bg-[#B1336E] text-white' : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="max-w-full truncate">{label}</span>
                  <span className="text-[10px] opacity-80">{count}</span>
                </button>
              ))}
            </div>
            {mobilePanel && (
              <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                <p className="text-xs font-bold text-slate-700">
                  {viewButtons.find((button) => button.id === mobilePanel)?.label}
                </p>
                <button
                  onClick={() => setMobilePanel(null)}
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
                  title="Ocultar panel"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {mobilePanel && <div className="h-[42vh] min-h-[260px] overflow-hidden">{mobileList}</div>}
          </div>
        </div>
      </main>

      {showMissingLocationModal && (
        <MissingLocationModal
          missingPoints={missingLocationPoints}
          onClose={() => setShowMissingLocationModal(false)}
        />
      )}

      {showNoResponseModal && (
        <NoResponseModal
          selectedDate={selectedDate}
          onCountLoaded={setNoResponseCount}
          onClose={() => setShowNoResponseModal(false)}
        />
      )}

      {showWhatsAppLocationsModal && (
        <WhatsAppLocationsModal
          selectedDate={selectedDate}
          onAlertCountChange={setWhatsAppLocationAlertCount}
          onClose={() => setShowWhatsAppLocationsModal(false)}
        />
      )}

      {showDriverSelectionModal && (
        <DriverSelectionModal
          drivers={drivers}
          onSelect={handleDriverAssignment}
          onClose={() => {
            setShowDriverSelectionModal(false);
            setSelectedPointsForDriver([]);
          }}
          selectedCount={selectedPointsForDriver.length}
        />
      )}

      {selectedItemId && selectedItemType && (
        <DetailModal
          itemId={selectedItemId}
          itemType={selectedItemType}
          onDeleted={handleDetailDeleted}
          onClose={() => {
            setSelectedItemId(null);
            setSelectedItemType(null);
          }}
        />
      )}

      {selectedUserId && (
        <UserDetailModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
      )}
    </div>
  );
}

export default TorreDeControl;
